import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { ArrowRight, Menu, X, ChevronDown } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { useBranding } from "./BrandingProvider";
import { useTicketAnchorProps } from "./TicketsProvider";

export function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [praktischOpen, setPraktischOpen] = useState(false);
  const { t, lang } = useI18n();
  const { logoFullUrl } = useBranding();
  const ticketAnchor = useTicketAnchorProps();
  const base = lang === "nl" ? "" : `/${lang}`;

  const simpleLinks = [
    { href: `${base}/#faq`, label: t.nav.faq },
  ];

  const praktischItems = [
    { href: `${base}/#praktisch`, label: t.nav.terrain },
    { href: `${base}/#bereikbaarheid`, label: t.nav.gettingHere },
    { href: `${base}/#weer`, label: t.nav.weatherComfort },
    { href: `${base}/#toegankelijkheid`, label: t.nav.accessibility },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 32);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    if (!open) setPraktischOpen(false);
  }, [open]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-40 transition-all duration-500 ${
        scrolled
          ? "bg-night-deep/85 backdrop-blur-xl border-b border-border/80 shadow-soft"
          : "bg-gradient-to-b from-night-deep/60 to-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 md:h-20 md:px-8">
        <Link
          to="/"
          onClick={() => {
            setOpen(false);
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          className="flex items-center group"
        >
          <img
            src={logoFullUrl}
            alt="Openlucht Filmfestival"
            width={1706}
            height={398}
            fetchPriority="high"
            decoding="async"
            className="h-10 w-auto md:h-12 transition-opacity group-hover:opacity-90"
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-9 md:flex">
          <Link
            to="/programma"
            className="relative text-[13px] font-medium text-foreground/75 transition-colors hover:text-gold"
          >
            {t.nav.programma}
          </Link>
          {/* Praktisch dropdown */}
          <div
            className="relative"
            onMouseEnter={() => setPraktischOpen(true)}
            onMouseLeave={() => setPraktischOpen(false)}
          >
            <button
              type="button"
              aria-haspopup="true"
              aria-expanded={praktischOpen}
              onClick={() => setPraktischOpen((v) => !v)}
              className="relative flex items-center gap-1 py-2 text-[13px] font-medium text-foreground/75 transition-colors hover:text-gold"
            >
              {t.nav.praktisch}
              <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${praktischOpen ? "rotate-180" : ""}`} />
            </button>
            {/* Invisible bridge so hover doesn't break in the gap */}
            <div
              className={`absolute left-1/2 top-full z-50 -translate-x-1/2 pt-2 ${
                praktischOpen ? "pointer-events-auto" : "pointer-events-none"
              }`}
            >
              <div
                role="navigation"
                aria-label={t.nav.praktisch}
                className={`w-56 rounded-lg border border-border/80 bg-night-deep/95 py-2 shadow-lg backdrop-blur-xl transition-all duration-200 ${
                  praktischOpen
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 -translate-y-1"
                }`}
              >
                {praktischItems.map((item) => (
                  <a
                    key={item.href}
                    href={item.href}
                    onClick={() => setPraktischOpen(false)}
                    className="block px-4 py-2 text-sm text-foreground/75 transition-colors hover:bg-secondary/50 hover:text-gold"
                  >
                    {item.label}
                  </a>
                ))}
              </div>
            </div>
          </div>

          {simpleLinks.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="relative text-[13px] font-medium text-foreground/75 transition-colors hover:text-gold"
            >
              {l.label}
            </a>
          ))}

          <Link
            to="/partners"
            className="relative text-[13px] font-medium text-foreground/75 transition-colors hover:text-gold"
          >
            {t.nav.partners}
          </Link>
        </nav>

        <div className="hidden items-center gap-3 md:flex">
          <span className="hidden font-mono-cinema text-[10px] text-foreground/75 lg:inline">{t.nav.dates}</span>
          <LanguageSwitcher variant="desktop" />
          <Button asChild variant="ghostLine" size="sm">
            <Link to="/vrijwilliger">{t.nav.volunteer}</Link>
          </Button>
          {ticketAnchor && (
            <Button asChild variant="hero" size="sm">
              <a {...ticketAnchor}>{t.nav.tickets} <ArrowRight className="h-3.5 w-3.5" /></a>
            </Button>
          )}
        </div>

        <button
          type="button"
          aria-label={open ? t.nav.menuClose : t.nav.menuOpen}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-border/70 bg-night-deep/60 text-foreground/85 backdrop-blur-md transition-colors hover:text-gold md:hidden"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Mobile menu overlay */}
      <div
        className={`fixed inset-x-0 top-16 z-30 origin-top border-b border-border/80 bg-night-deep/95 backdrop-blur-xl transition-all duration-300 md:hidden ${
          open ? "pointer-events-auto opacity-100 translate-y-0" : "pointer-events-none -translate-y-2 opacity-0"
        }`}
      >
        <nav className="mx-auto flex max-w-7xl flex-col gap-1 px-5 py-6">
          <Link
            to="/programma"
            onClick={() => setOpen(false)}
            className="rounded-md px-3 py-3 font-display text-lg text-foreground/85 transition-colors hover:bg-secondary hover:text-gold"
          >
            {t.nav.programma}
          </Link>
          {/* Praktisch mobile */}
          <div>
            <button
              type="button"
              onClick={() => setPraktischOpen((v) => !v)}
              className="flex w-full items-center justify-between rounded-md px-3 py-3 font-display text-lg text-foreground/85 transition-colors hover:bg-secondary hover:text-gold"
            >
              <span>{t.nav.praktisch}</span>
              <ChevronDown className={`h-5 w-5 transition-transform duration-200 ${praktischOpen ? "rotate-180" : ""}`} />
            </button>
            <div
              className={`overflow-hidden transition-all duration-300 ${
                praktischOpen ? "max-h-48 opacity-100" : "max-h-0 opacity-0"
              }`}
            >
              <div className="flex flex-col gap-1 pl-4 pt-1">
                {praktischItems.map((item) => (
                  <a
                    key={item.href}
                    href={item.href}
                    onClick={() => {
                      setOpen(false);
                      setPraktischOpen(false);
                    }}
                    className="rounded-md px-3 py-2.5 text-base text-foreground/65 transition-colors hover:bg-secondary hover:text-gold"
                  >
                    {item.label}
                  </a>
                ))}
              </div>
            </div>
          </div>

          <a
            href={`${base}/#faq`}
            onClick={() => setOpen(false)}
            className="rounded-md px-3 py-3 font-display text-lg text-foreground/85 transition-colors hover:bg-secondary hover:text-gold"
          >
            {t.nav.faq}
          </a>

          <Link
            to="/partners"
            onClick={() => setOpen(false)}
            className="rounded-md px-3 py-3 font-display text-lg text-foreground/85 transition-colors hover:bg-secondary hover:text-gold"
          >
            {t.nav.partners}
          </Link>

          <Link
            to="/vrijwilliger"
            onClick={() => setOpen(false)}
            className="rounded-md px-3 py-3 font-display text-lg text-gold transition-colors hover:bg-secondary"
          >
            {t.nav.volunteer}
          </Link>

          <div className="mt-3 flex items-center justify-between gap-3 border-t border-border/60 pt-4">
            <span className="font-mono-cinema text-[10px] text-foreground/75">{t.common.language}</span>
            <LanguageSwitcher variant="mobile" />
          </div>

          <div className="mt-3 flex items-center justify-between gap-3 border-t border-border/60 pt-4">
            <span className="font-mono-cinema text-[10px] text-foreground/75">{t.nav.dates}</span>
            {ticketAnchor && (
              <Button asChild variant="hero" size="sm" onClick={() => setOpen(false)}>
                <a {...ticketAnchor}>{t.nav.tickets} <ArrowRight className="h-3.5 w-3.5" /></a>
              </Button>
            )}
          </div>
        </nav>
      </div>
    </header>
  );
}
